import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectCardData, selectChecklistData } from '../../../../store/card/card.selector';
import { selectUser } from '../../../../store/user/user.selector';
import { fetchCollectionData, fetchChecklistDataByIds, fetchChecklistCardIds } from '../../../../utils/firebase/firebase.utils';
import { getAvailableSeries, getAvailableCardTypes, getAvailableCardTypeCategories } from '../../../../utils/firebase/checklist';
import ChecklistItemDisplay from '../../../../components/checklist-item-display/ChecklistItemDisplay';
import InputDropdown from '../../../../components/input-dropdown/InputDropdown';

// styles
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  NavigationContainer,
  Breadcrumb,
  BreadcrumbItem,
  CardsGrid,
  LoadingContainer
} from './PSACardSelectionModal.styles';

export default function PSACardSelectionModal({ 
  isOpen, 
  onClose,
  onCardSelect,
  gradingData, // { type: 'graded' | 'new', grade?: string, service?: string }
  isSaving = false
}) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const cardData = useSelector(selectCardData);
  const checklistData = useSelector(selectChecklistData);
  
  const [navigationPath, setNavigationPath] = useState(['MLB', '2025', 'Topps']);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [availableSeries, setAvailableSeries] = useState([]);
  const [availableCardTypes, setAvailableCardTypes] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState('');
  const [selectedCardType, setSelectedCardType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const [sport, year, manufacturer] = navigationPath;

  // Fetch available series
  useEffect(() => {
    const fetchSeries = async () => {
      if (isOpen && user?.uid && sport && year && manufacturer) {
        setIsLoading(true);
        try {
          const seriesData = await getAvailableSeries(user.uid, sport, year, manufacturer);
          // seriesData is an object where keys are series names
          const seriesList = Object.keys(seriesData || {}).sort();
          setAvailableSeries(seriesList);
        } catch (error) {
          console.error('Error fetching series:', error);
          setAvailableSeries([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchSeries();
  }, [isOpen, user?.uid, sport, year, manufacturer]);

  // Fetch available card types when series is selected
  useEffect(() => {
    const fetchCardTypes = async () => {
      if (isOpen && user?.uid && sport && year && manufacturer && selectedSeries) {
        setIsLoading(true);
        try {
          const cardTypesData = await getAvailableCardTypes(user.uid, sport, year, manufacturer, selectedSeries);
          const cardTypesList = cardTypesData.map(ct => ct.cardType).sort();
          setAvailableCardTypes(cardTypesList);
        } catch (error) {
          console.error('Error fetching card types:', error);
          setAvailableCardTypes([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchCardTypes();
  }, [isOpen, user?.uid, sport, year, manufacturer, selectedSeries]);

  // Fetch available categories when card type is selected
  useEffect(() => {
    const fetchCategories = async () => {
      if (isOpen && user?.uid && sport && year && manufacturer && selectedSeries && selectedCardType) {
        setIsLoading(true);
        try {
          const categoriesData = await getAvailableCardTypeCategories(user.uid, sport, year, manufacturer, selectedSeries, selectedCardType);
          // categoriesData is an object where keys are category names
          const categoriesList = Object.keys(categoriesData || {}).sort();
          setAvailableCategories(categoriesList);
        } catch (error) {
          console.error('Error fetching categories:', error);
          setAvailableCategories([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchCategories();
  }, [isOpen, user?.uid, sport, year, manufacturer, selectedSeries, selectedCardType]);

  // Fetch collection data when we have a full path
  useEffect(() => {
    if (isOpen && user?.uid && sport && year && manufacturer && selectedSeries && selectedCardType && selectedCategory) {
      setIsLoading(true);
      const collectionPath = `users/${user.uid}/collection/${sport}/${year}/${manufacturer}/${selectedSeries}/${selectedCardType}/${selectedCategory}`;
      
      Promise.all([
        dispatch(fetchCollectionData(collectionPath)),
        dispatch(fetchChecklistCardIds(sport, year, manufacturer, selectedSeries, selectedCardType, selectedCategory))
      ]).then(([, cardIds]) => {
        if (cardIds && cardIds.length > 0) {
          return dispatch(fetchChecklistDataByIds(sport, year, manufacturer, selectedSeries, selectedCardType, selectedCategory, cardIds));
        }
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [isOpen, user?.uid, sport, year, manufacturer, selectedSeries, selectedCardType, selectedCategory, dispatch]);

  // Get collected cards for display
  const collectedCards = useMemo(() => {
    if (!cardData || Object.keys(cardData).length === 0) return [];
    
    return Object.values(cardData)
      .filter(card => card.collectedCards && card.collectedCards.length > 0)
      .map(card => {
        // Merge with checklist data if available
        const checklistCard = checklistData[card.cardNumber];
        const mergedCard = checklistCard
          ? { ...checklistCard, ...card, isCollected: true }
          : { ...card, isCollected: true };
        return mergedCard;
      });
  }, [cardData, checklistData]);

  const handleBreadcrumbClick = (index) => {
    // Handle clicking on navigationPath items (sport, year, manufacturer)
    setNavigationPath(prev => prev.slice(0, index + 1));
    if (index < 2) {
      setSelectedSeries('');
      setSelectedCardType('');
      setSelectedCategory('');
    }
  };

  const handleSeriesBreadcrumbClick = () => {
    // Clicking on series resets cardType and category
    setSelectedCardType('');
    setSelectedCategory('');
    setSelectedCard(null);
  };

  const handleCardTypeBreadcrumbClick = () => {
    // Clicking on cardType resets category
    setSelectedCategory('');
    setSelectedCard(null);
  };

  const handleCategoryBreadcrumbClick = () => {
    // Clicking on category just clears selected card
    setSelectedCard(null);
  };

  const handleSeriesChange = (seriesName) => {
    setSelectedSeries(seriesName);
    setSelectedCardType('');
    setSelectedCategory('');
  };

  const handleCardTypeChange = (cardTypeName) => {
    setSelectedCardType(cardTypeName);
    setSelectedCategory('');
  };

  const handleCategoryChange = (categoryName) => {
    setSelectedCategory(categoryName);
  };

  const handleCardSelect = (cardData) => {
    // cardData includes the card and selectedVariation (if a specific variation was selected)
    setSelectedCard(cardData);
  };

  const handleConfirm = () => {
    if (selectedCard && onCardSelect) {
      onCardSelect({
        card: selectedCard,
        gradingData
      });
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedCard(null);
    setNavigationPath(['MLB', '2025', 'Topps']);
    setSelectedSeries('');
    setSelectedCardType('');
    setSelectedCategory('');
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={handleClose} className='widget-bg'>
      <ModalContainer onClick={(e) => e.stopPropagation()} className='panel-bg'>
        <ModalHeader>
          <ModalTitle>Select Card from Collection</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <NavigationContainer>
            <Breadcrumb>
              {navigationPath.map((item, index) => (
                <BreadcrumbItem 
                  key={index}
                  onClick={() => handleBreadcrumbClick(index)}
                  $isActive={index === navigationPath.length - 1 && !selectedSeries}
                >
                  {item}
                  {' / '}
                </BreadcrumbItem>
              ))}
              {selectedSeries && (
                <BreadcrumbItem 
                  onClick={handleSeriesBreadcrumbClick}
                  $isActive={!selectedCardType}
                >
                  {selectedSeries}
                  {selectedCardType && ' / '}
                </BreadcrumbItem>
              )}
              {selectedCardType && (
                <BreadcrumbItem 
                  onClick={handleCardTypeBreadcrumbClick}
                  $isActive={!selectedCategory}
                >
                  {selectedCardType}
                  {selectedCategory && ' / '}
                </BreadcrumbItem>
              )}
              {selectedCategory && (
                <BreadcrumbItem 
                  onClick={handleCategoryBreadcrumbClick}
                  $isActive={true}
                >
                  {selectedCategory}
                </BreadcrumbItem>
              )}
            </Breadcrumb>
          </NavigationContainer>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
            {selectedSeries === '' && (
              <div>
                <label style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  Select Series
                </label>
                <InputDropdown
                  id="series-select"
                  label="Series"
                  value={selectedSeries}
                  onChange={handleSeriesChange}
                  options={availableSeries}
                  placeholder="Select a series"
                />
              </div>
            )}

            {selectedSeries && selectedCardType === '' && (
              <div>
                <label style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  Select Card Type
                </label>
                <InputDropdown
                  id="card-type-select"
                  label="Card Type"
                  value={selectedCardType}
                  onChange={handleCardTypeChange}
                  options={availableCardTypes}
                  placeholder="Select a card type"
                />
              </div>
            )}

            {selectedSeries && selectedCardType && selectedCategory === '' && (
              <div>
                <label style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  Select Category
                </label>
                <InputDropdown
                  id="category-select"
                  label="Category"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  options={availableCategories}
                  placeholder="Select a category"
                />
              </div>
            )}
          </div>

          {isLoading ? (
            <LoadingContainer>
              <p>Loading cards...</p>
            </LoadingContainer>
          ) : selectedSeries && selectedCardType && selectedCategory && collectedCards.length > 0 ? (
            <CardsGrid>
              {collectedCards.map((card) => (
                <div 
                  key={card.cardNumber}
                  style={{
                    border: selectedCard?.cardNumber === card.cardNumber 
                      ? '2px solid #4CAF50' 
                      : '1px solid rgba(255,255,255,0.5)',
                    borderRadius: '8px',
                    padding: '4px',
                    transition: 'border-color 0.2s ease'
                  }}
                >
                  <ChecklistItemDisplay
                    card={card}
                    showcaseView={true}
                    onCardSelect={handleCardSelect}
                  />
                </div>
              ))}
            </CardsGrid>
          ) : selectedSeries && selectedCardType && selectedCategory && collectedCards.length === 0 ? (
            <LoadingContainer>
              <p>No cards found in this category.</p>
            </LoadingContainer>
          ) : (
            <LoadingContainer>
              <p>Select a series, card type, and category to view cards.</p>
            </LoadingContainer>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <button
              onClick={handleConfirm}
              disabled={!selectedCard || isSaving}
              style={{
                background: selectedCard && !isSaving ? 'linear-gradient(135deg, #4CAF50, #45a049)' : 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '8px',
                color: '#FFFFFF',
                cursor: selectedCard && !isSaving ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 600,
                padding: '12px 24px',
                transition: 'all 0.3s ease',
                minWidth: '120px',
                opacity: selectedCard && !isSaving ? 1 : 0.5
              }}
            >
              {isSaving ? 'Saving...' : 'Confirm Selection'}
            </button>
            <button 
              onClick={handleClose}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#FFFFFF',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                padding: '12px 24px',
                transition: 'all 0.3s ease',
                minWidth: '120px'
              }}
            >
              Cancel
            </button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
